import { useState, useEffect, useRef } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { useAuth } from '@/hooks/useAuth';
import { TurnstileWebView } from '@/components/TurnstileWebView';

const TURNSTILE_SITE_KEY = process.env.EXPO_PUBLIC_TURNSTILE_SITE_KEY ?? '';
const IS_WEB = Platform.OS === 'web';
const CAPTCHA_REQUIRED_WEB    = IS_WEB && !!TURNSTILE_SITE_KEY;
const CAPTCHA_REQUIRED_MOBILE = !IS_WEB && !!TURNSTILE_SITE_KEY;
const CAPTCHA_REQUIRED        = CAPTCHA_REQUIRED_WEB || CAPTCHA_REQUIRED_MOBILE;

interface TurnstileGlobal {
  render: (
    el: HTMLElement | string,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      theme?: 'light' | 'dark' | 'auto';
      size?: 'normal' | 'compact';
    }
  ) => string;
  reset: (widgetId?: string) => void;
}

export default function LoginScreen() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0); // muda -> remonta o WebView do Turnstile (token novo)
  const widgetRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Carrega script Cloudflare Turnstile dinamicamente (mais robusto que depender de +html.tsx
  // que nao foi incluido em alguns exports web). Renderiza widget assim que disponivel.
  useEffect(() => {
    if (!CAPTCHA_REQUIRED_WEB) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    let scriptInjected = false;
    if (!document.querySelector(`script[src="${TURNSTILE_SCRIPT_URL}"]`)) {
      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      scriptInjected = true;
    }

    let tries = 0;
    const interval = setInterval(() => {
      tries++;
      const ts = (window as unknown as { turnstile?: TurnstileGlobal }).turnstile;
      if (!ts || !widgetRef.current || widgetIdRef.current) {
        if (tries > 100) clearInterval(interval); // 10s timeout
        return;
      }
      widgetIdRef.current = ts.render(widgetRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'light',
        callback: (token) => setCaptchaToken(token),
        'error-callback': () => setCaptchaToken(null),
        'expired-callback': () => setCaptchaToken(null),
      });
      clearInterval(interval);
    }, 100);

    return () => {
      clearInterval(interval);
      // Nao removemos o script porque outros mounts (re-render) podem precisar
      void scriptInjected;
    };
  }, []);

  async function handleLogin() {
    if (!email || !password) {
      setError('Preencha email e senha');
      return;
    }
    if (CAPTCHA_REQUIRED && !captchaToken) {
      setError('Complete o captcha antes de entrar');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await signIn(email, password, captchaToken ?? undefined);
    } catch (e: unknown) {
      const msg: string = (e as { message?: string })?.message ?? '';
      if (msg.toLowerCase().includes('invalid login credentials') || msg.toLowerCase().includes('invalid email or password'))
        setError('Email ou senha incorretos');
      else if (msg.toLowerCase().includes('email not confirmed'))
        setError('Email não confirmado. Verifique sua caixa de entrada.');
      else if (msg.toLowerCase().includes('captcha'))
        setError('Captcha expirou ou falhou. Tente novamente.');
      else
        setError(msg || 'Erro ao entrar. Tente novamente.');
      // Reseta o captcha pra obter um token NOVO. O token do Turnstile é de USO ÚNICO
      // e curto: reusar o mesmo após uma falha dá "timeout-or-duplicate" (o "Captcha
      // expirou ou falhou" em loop). Antes isso só acontecia no web — o mobile ficava
      // preso reusando o token gasto.
      setCaptchaToken(null);
      if (CAPTCHA_REQUIRED_WEB && typeof window !== 'undefined') {
        const ts = (window as unknown as { turnstile?: TurnstileGlobal }).turnstile;
        if (ts && widgetIdRef.current) ts.reset(widgetIdRef.current);
      } else if (CAPTCHA_REQUIRED_MOBILE) {
        setCaptchaKey(k => k + 1); // remonta o WebView -> token novo
      }
    } finally {
      setLoading(false);
    }
  }

  const submitDisabled = loading || (CAPTCHA_REQUIRED && !captchaToken);

  return (
    <View style={styles.container}>
      <Text style={styles.title}>AraFit</Text>
      <Text style={styles.subtitle}>Entre na sua conta</Text>
      <TextInput
        style={styles.input}
        placeholder="Email"
        placeholderTextColor="#999"
        value={email}
        onChangeText={setEmail}
        autoCapitalize="none"
        keyboardType="email-address"
        returnKeyType="next"
      />
      <TextInput
        style={styles.input}
        placeholder="Senha"
        placeholderTextColor="#999"
        value={password}
        onChangeText={setPassword}
        secureTextEntry
        returnKeyType="done"
        onSubmitEditing={handleLogin}
      />

      {/* Turnstile widget. Web: ref + script via <Script>. Mobile: WebView. */}
      {CAPTCHA_REQUIRED_WEB && (
        <View
          ref={widgetRef as unknown as React.RefObject<View>}
          style={styles.captchaWrap}
        />
      )}
      {CAPTCHA_REQUIRED_MOBILE && (
        <TurnstileWebView
          key={captchaKey}
          siteKey={TURNSTILE_SITE_KEY}
          onToken={setCaptchaToken}
          onError={() => setCaptchaToken(null)}
        />
      )}

      {error ? <Text style={styles.error}>{error}</Text> : null}
      <TouchableOpacity
        style={[styles.button, submitDisabled && styles.buttonDisabled]}
        onPress={handleLogin}
        disabled={submitDisabled}
      >
        <Text style={styles.buttonText}>{loading ? 'Entrando...' : 'Entrar'}</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, justifyContent: 'center', padding: 24, backgroundColor: '#fff' },
  title: { fontSize: 36, fontWeight: 'bold', color: '#C8102E', textAlign: 'center', marginBottom: 8 },
  subtitle: { fontSize: 16, color: '#666', textAlign: 'center', marginBottom: 32 },
  input: { borderWidth: 1, borderColor: '#ddd', borderRadius: 8, padding: 14, marginBottom: 12, fontSize: 16, color: '#1A1A1A', backgroundColor: '#fff' },
  captchaWrap: { marginBottom: 12, alignItems: 'center' },
  error: { color: '#C8102E', fontSize: 14, marginBottom: 12, textAlign: 'center' },
  button: { backgroundColor: '#C8102E', padding: 16, borderRadius: 8, alignItems: 'center' },
  buttonDisabled: { opacity: 0.6 },
  buttonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },
});
